/**
 * 小程序运行时缺失的 Web API 最小占位实现（WIP）。
 *
 * 背景：核心 SDK（`src/http/fetch.ts` 的 `createCloudFetch`）直接 `new Headers(...)`，
 * 不是通过注入口子拿到的 —— 这是核心 SDK 对"浏览器环境"的一处硬依赖，不是本
 * 适配层能绕开的。因此本模块在小程序里补一份全局 `Headers`；`ReadableStream`
 * 同理，`iterSSEEvents`（`modules/llm/sse.ts`）直接调 `body.getReader()`。
 *
 * ⚠️ 占位说明：以下两个类都只实现了 SDK 自己会用到的方法子集，不是 spec 完整
 * 实现（例如 `MiniProgramReadableStream` 不支持 tee/pipeTo，也不是真正的
 * 生产者暂停式 backpressure——只是按字节数设了一个缓冲上限，超限直接 abort
 * 整条流，把「内存持续膨胀」变成「明确报错」）。
 * 只在目标小程序基础库确实缺失对应全局对象时才会安装，且没有验证过真机基础库
 * 版本号 —— 接入时用 `wx.getSystemInfoSync` 之类的方式确认目标基础库是否已经
 * 自带这两个全局对象，自带的话应该跳过这份占位，直接用原生实现更可靠。
 */

/** 大小写不敏感的最小 Headers 占位实现。 */
class MiniProgramHeaders {
    private readonly map = new Map<string, string>();

    constructor(init?: HeadersInit) {
        if (!init) return;
        if (init instanceof MiniProgramHeaders) {
            for (const [key, value] of init.map) this.map.set(key, value);
            return;
        }
        if (Array.isArray(init)) {
            for (const [key, value] of init) this.set(key, value);
            return;
        }
        if (init instanceof Headers) {
            init.forEach((value, key) => this.set(key, value));
            return;
        }
        for (const [key, value] of Object.entries(init)) this.set(key, String(value));
    }

    private normalize(name: string): string {
        return name.toLowerCase();
    }

    set(name: string, value: string): void {
        this.map.set(this.normalize(name), value);
    }

    get(name: string): string | null {
        return this.map.get(this.normalize(name)) ?? null;
    }

    has(name: string): boolean {
        return this.map.has(this.normalize(name));
    }

    append(name: string, value: string): void {
        const key = this.normalize(name);
        const existing = this.map.get(key);
        this.map.set(key, existing ? `${existing}, ${value}` : value);
    }

    delete(name: string): void {
        this.map.delete(this.normalize(name));
    }

    forEach(callback: (value: string, key: string) => void): void {
        this.map.forEach((value, key) => callback(value, key));
    }

    entries(): IterableIterator<[string, string]> {
        return this.map.entries();
    }

    /** 转成 `wx.request` 需要的 plain object header。 */
    toPlainObject(): Record<string, string> {
        return Object.fromEntries(this.map);
    }
}

/** 流式响应队列默认字节上限（4 MiB）：超过后中止流并向 reader 抛错，
 *  而不是无限缓冲——慢消费者或异常长响应会持续占用小程序有限的运行时内存。
 */
const DEFAULT_MAX_BUFFERED_BYTES = 4 * 1024 * 1024;

/**
 * 极简 ReadableStream 占位：单读者、按到达顺序 push，不支持 tee/pipeTo。
 *
 * 只满足 `iterSSEEvents` 的调用面：`getReader().read()` / `.cancel()` / `.releaseLock()`。
 *
 * 背压：队列按字节数计数，超过 `maxBufferedBytes` 时整条流直接 `abort`——
 * 不是真正的生产者暂停式背压（占位实现做不到暂停 `wx.request` 的分块推送），
 * 但至少把「内存持续膨胀」变成「明确报错」，调用方能感知并重试/降级。
 */
class MiniProgramReadableStream {
    private readonly queue: Uint8Array[] = [];
    private queuedBytes = 0;
    private readonly maxBufferedBytes: number;
    private closed = false;
    private error: Error | null = null;
    private pendingResolve: ((result: { done: boolean; value?: Uint8Array }) => void) | null = null;
    private pendingReject: ((err: Error) => void) | null = null;
    private cancelled = false;

    constructor(options?: { maxBufferedBytes?: number }) {
        this.maxBufferedBytes = options?.maxBufferedBytes ?? DEFAULT_MAX_BUFFERED_BYTES;
    }

    /** 供 fetch 适配层喂数据用，不是公开 API。超过字节上限时自动 abort。 */
    push(chunk: Uint8Array): void {
        if (this.cancelled || this.closed || this.error) return;
        if (this.queuedBytes + chunk.byteLength > this.maxBufferedBytes) {
            this.abort(
                new Error(
                    `MiniProgramReadableStream: buffered bytes exceeded limit (${this.maxBufferedBytes}), aborting stream`,
                ),
            );
            return;
        }
        this.queuedBytes += chunk.byteLength;
        if (this.pendingResolve) {
            const resolve = this.pendingResolve;
            this.pendingResolve = null;
            this.pendingReject = null;
            resolve({ done: false, value: chunk });
        } else {
            this.queue.push(chunk);
        }
    }

    /** 供 fetch 适配层在请求正常结束时调用，不是公开 API。 */
    close(): void {
        if (this.closed || this.error) return;
        this.closed = true;
        if (this.pendingResolve) {
            const resolve = this.pendingResolve;
            this.pendingResolve = null;
            this.pendingReject = null;
            resolve({ done: true });
        }
    }

    /**
     * 供 fetch 适配层在检测到「流已经开始但实际是错误响应/请求异常终止」
     * 时调用，不是公开 API。跟 `close()` 的区别：`close()` 是正常 EOF，
     * `abort()` 让当前挂起的 `read()` 与后续所有 `read()` 都 reject，
     * 避免调用方把截断的错误响应体误判为一段正常收完的 SSE 内容。
     */
    abort(err: Error): void {
        if (this.closed || this.error) return;
        this.error = err;
        this.closed = true;
        this.queue.length = 0;
        this.queuedBytes = 0;
        if (this.pendingReject) {
            const reject = this.pendingReject;
            this.pendingResolve = null;
            this.pendingReject = null;
            reject(err);
        }
    }

    getReader(): {
        read(): Promise<{ done: boolean; value?: Uint8Array }>;
        cancel(): Promise<void>;
        releaseLock(): void;
    } {
        return {
            read: () => {
                if (this.cancelled) {
                    return Promise.resolve({ done: true });
                }
                if (this.queue.length > 0) {
                    const value = this.queue.shift();
                    if (value) this.queuedBytes -= value.byteLength;
                    return Promise.resolve({ done: false, value });
                }
                if (this.error) {
                    return Promise.reject(this.error);
                }
                if (this.closed) {
                    return Promise.resolve({ done: true });
                }
                return new Promise((resolve, reject) => {
                    this.pendingResolve = resolve;
                    this.pendingReject = reject;
                });
            },
            cancel: () => {
                this.cancelled = true;
                this.queue.length = 0;
                this.queuedBytes = 0;
                return Promise.resolve();
            },
            releaseLock: () => {
                // 单读者占位实现，无需真正释放锁。
            },
        };
    }
}

/** 是否已经具备（原生或已安装占位）小程序流式响应所需的 `ReadableStream`。 */
export function isStreamingSupported(): boolean {
    return typeof (globalThis as { ReadableStream?: unknown }).ReadableStream !== 'undefined';
}

/**
 * 按需安装 `Headers` / `ReadableStream` 占位实现到 `globalThis`。
 *
 * 幂等：已存在同名全局对象时跳过，不覆盖原生实现。
 * 必须在调用 `createWorkBuddyCloud(...)` 之前执行一次。
 */
export function ensureMiniProgramPolyfills(): void {
    const g = globalThis as Record<string, unknown>;
    if (typeof g.Headers === 'undefined') {
        g.Headers = MiniProgramHeaders as unknown;
    }
    if (typeof g.ReadableStream === 'undefined') {
        g.ReadableStream = MiniProgramReadableStream as unknown;
    }
}

export { MiniProgramHeaders, MiniProgramReadableStream };
